import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, PackageCheck, AlertTriangle, ChevronLeft, ChevronRight, ArrowDownAZ, ArrowUpAZ } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useDebouncedValue } from "../lib/useDebouncedValue";
import { ClearableInput } from "../components/common/ClearableInput";
import { SearchableSelect } from "../components/common/SearchableSelect";
import { HeaderFilterSelect } from "../components/common/DataTable";
import { useAuth } from "../context/AuthContext";
import { useWorkspace } from "../context/WorkspaceContext";
import { PageHeader } from "../components/common/PageHeader";
import { ProjectScopeBanner } from "../components/common/ProjectScopeBanner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function GrnWorklist() {
  const { has } = useAuth();
  const { ctx, dataVersion } = useWorkspace();
  const projectId = ctx.project?.id || "";
  const [types, setTypes] = useState([]);
  const [makes, setMakes] = useState([]);
  const [stores, setStores] = useState([]);
  const [users, setUsers] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [typeId, setTypeId] = useState("__all__");
  const [makeId, setMakeId] = useState("");
  const [serialFrom, setSerialFrom] = useState("");
  const [serialTo, setSerialTo] = useState("");
  const [sortDir, setSortDir] = useState("asc");
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pendingByType, setPendingByType] = useState({});
  const [pendingTotal, setPendingTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState(new Set());
  const [grnOpen, setGrnOpen] = useState(false);
  const [defOpen, setDefOpen] = useState(false);
  const [storeId, setStoreId] = useState("");
  const [recDate, setRecDate] = useState(new Date().toISOString().slice(0, 10));
  const [checkerId, setCheckerId] = useState("");
  const [reasonId, setReasonId] = useState("");
  const [remarks, setRemarks] = useState("");
  const dSearch = useDebouncedValue(search);
  const dSerialFrom = useDebouncedValue(serialFrom);
  const dSerialTo = useDebouncedValue(serialTo);
  const loadSeq = useRef(0);
  const pageSize = 100;

  useEffect(() => {
    api.get("/material-types", { params: { page_size: 500 } }).then((r) => setTypes(r.data.items || []));
    api.get("/material-makes", { params: { page_size: 500 } }).then((r) => setMakes(r.data.items || [])).catch(() => {});
    api.get("/users", { params: { page_size: 500 } }).then((r) => setUsers(r.data.items || [])).catch(() => {});
    api.get("/defect-reasons").then((r) => setReasons(r.data.items || [])).catch(() => {});
  }, [dataVersion]);
  useEffect(() => {
    if (projectId) api.get("/stores", { params: { project_id: projectId, page_size: 500 } }).then((r) => setStores(r.data.items || []));
  }, [projectId, dataVersion]);

  const load = useCallback(async () => {
    if (!projectId) { setItems([]); setTotal(0); return; }
    setLoading(true);
    try {
      const params = { project_id: projectId, page, page_size: pageSize, search: dSearch, sort: "serial", dir: sortDir };
      if (typeId !== "__all__") params.material_type_id = typeId;
      if (makeId) params.make_id = makeId;
      if (dSerialFrom) params.serial_from = dSerialFrom;
      if (dSerialTo) params.serial_to = dSerialTo;
      const reqId = ++loadSeq.current;   // latest-wins: stale responses never overwrite newer ones
      const { data } = await api.get("/grn/worklist", { params });
      if (reqId !== loadSeq.current) return;
      setItems(data.items || []); setTotal(data.total || 0);
      setPendingByType(data.pending_by_type || {}); setPendingTotal(data.pending_total || 0);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load worklist"); }
    finally { setLoading(false); }
  }, [projectId, typeId, makeId, dSerialFrom, dSerialTo, sortDir, dSearch, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSel(new Set()); }, [projectId]);  // selection survives paging/filtering so "select all filtered" persists

  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const togglePage = () => setSel((s) => { const all = items.every((m) => s.has(m.id)); const n = new Set(s); items.forEach((m) => all ? n.delete(m.id) : n.add(m.id)); return n; });
  const allPageSel = items.length > 0 && items.every((m) => sel.has(m.id));
  const [selAllLoading, setSelAllLoading] = useState(false);
  const selectAllFiltered = async () => {
    setSelAllLoading(true);
    try {
      const ids = new Set(sel);
      let p = 1;
      for (;;) {
        const params = { project_id: projectId, page: p, page_size: 500, search: dSearch, sort: "serial", dir: sortDir };
        if (typeId !== "__all__") params.material_type_id = typeId;
        if (makeId) params.make_id = makeId;
        if (dSerialFrom) params.serial_from = dSerialFrom;
        if (dSerialTo) params.serial_to = dSerialTo;
        const { data } = await api.get("/grn/worklist", { params });
        (data.items || []).forEach((m) => ids.add(m.id));
        if (!data.has_more || ids.size >= 5000) break;
        p += 1;
      }
      setSel(ids);
      toast.success(`Selected ${ids.size} materials matching the current filter`);
    } catch { toast.error("Failed to select all filtered materials"); }
    finally { setSelAllLoading(false); }
  };

  const doGrn = async () => {
    if (!storeId || !recDate) { toast.error("Store and received date required"); return; }
    try {
      const { data } = await api.post("/grn", { material_ids: [...sel], store_id: storeId, actual_received_date: recDate });
      toast.success(`GRN: ${data.success} completed, ${data.failed} skipped`);
      setGrnOpen(false); setSel(new Set()); load();
    } catch (e) { toast.error(e.response?.data?.detail || "GRN failed"); }
  };
  const doDefective = async () => {
    if (!checkerId) { toast.error("Select a checker"); return; }
    try {
      const { data } = await api.post("/materials/defective", { material_ids: [...sel], checker_id: checkerId, defect_reason_id: reasonId || null, remarks });
      toast.success(`Defective: ${data.success} marked, ${data.failed} skipped`);
      setDefOpen(false); setSel(new Set()); setRemarks(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div>
      <PageHeader title="GRN Worklist" subtitle="GRN-pending materials for the active project — single or bulk goods receipt" />
      <ProjectScopeBanner requireProject action="GRN" />
      {projectId && (
        <div className="mb-4" data-testid="grn-pending-by-type">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Pending by Type · <span className="text-slate-700">Total {pendingTotal}</span></div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
            {Object.keys(pendingByType).length === 0 ? (
              <span className="text-sm text-slate-400">No pending materials</span>
            ) : Object.entries(pendingByType).sort((a, b) => b[1] - a[1]).map(([t, c]) => (
              <div key={t} data-testid={`grn-pending-card-${t}`} className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2">
                <div className="truncate text-[11px] font-medium uppercase tracking-wide text-blue-700" title={t}>{t}</div>
                <div className="text-xl font-bold tabular-nums text-blue-900">{c}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      {projectId && (
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div><Label className="text-xs text-slate-500">Serial from</Label><ClearableInput testid="grn-serial-from" value={serialFrom} onChange={(v) => { setPage(1); setSerialFrom(v); }} placeholder="e.g. 0001" className="mt-1 w-36" /></div>
        <div><Label className="text-xs text-slate-500">Serial to</Label><ClearableInput testid="grn-serial-to" value={serialTo} onChange={(v) => { setPage(1); setSerialTo(v); }} placeholder="e.g. 9999" className="mt-1 w-36" /></div>
        <ClearableInput testid="grn-search" value={search} onChange={(v) => { setPage(1); setSearch(v); }} placeholder="Search serial..." className="sm:max-w-xs" />
      </div>
      )}

      {sel.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5">
          <span className="text-sm font-medium text-blue-900" data-testid="grn-selected-count">{sel.size} selected</span>
          {has("MATERIAL.GRN") && <Button data-testid="grn-action-btn" size="sm" onClick={() => setGrnOpen(true)} className="bg-blue-600 hover:bg-blue-700"><PackageCheck className="mr-1.5 h-4 w-4" /> GRN Selected</Button>}
          {has("MATERIAL.DEFECTIVE_MARK") && <Button data-testid="defective-action-btn" size="sm" variant="outline" onClick={() => setDefOpen(true)}><AlertTriangle className="mr-1.5 h-4 w-4 text-amber-600" /> Mark Defective</Button>}
          <Button size="sm" variant="ghost" onClick={() => setSel(new Set())}>Clear</Button>
        </div>
      )}

      {allPageSel && total > items.length && sel.size < total && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800" data-testid="grn-select-all-filtered">
          <span>All {items.length} on this page are selected.</span>
          <Button size="sm" variant="outline" className="border-blue-300 bg-white text-blue-700" disabled={selAllLoading} onClick={selectAllFiltered} data-testid="grn-select-all-filtered-btn">
            {selAllLoading ? "Selecting…" : `Select all ${total} matching this filter`}
          </Button>
        </div>
      )}
      {total > items.length && sel.size >= total && (
        <div className="mb-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800" data-testid="grn-all-filtered-selected">All {total} materials matching this filter are selected.</div>
      )}
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="w-full min-w-[640px] text-left">
          <thead><tr className="border-b border-slate-200 bg-slate-100/80">
            <th className="px-3 py-3"><Checkbox data-testid="grn-select-page" checked={items.length > 0 && items.every((m) => sel.has(m.id))} onCheckedChange={togglePage} /></th>
            <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
              <button type="button" data-testid="grn-sort-serial" onClick={() => { setPage(1); setSortDir((d) => (d === "asc" ? "desc" : "asc")); }} className="flex items-center gap-1 uppercase tracking-wider hover:text-slate-700">
                Serial {sortDir === "asc" ? <ArrowDownAZ className="h-3.5 w-3.5" /> : <ArrowUpAZ className="h-3.5 w-3.5" />}
              </button>
            </th>
            <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
              <div className="flex items-center gap-1"><span>Type</span>
                <HeaderFilterSelect value={typeId === "__all__" ? "" : typeId} options={types.map((t) => ({ value: t.id, label: t.type_name }))} onChange={(v) => { setPage(1); setTypeId(v || "__all__"); }} />
              </div>
            </th>
            <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
              <div className="flex items-center gap-1"><span>Make</span>
                <HeaderFilterSelect value={makeId} options={makes.map((m) => ({ value: m.id, label: m.name }))} onChange={(v) => { setPage(1); setMakeId(v); }} />
              </div>
            </th>
            <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Uploaded Rcvd</th>
          </tr></thead>
          <tbody data-testid="grn-rows">
            {loading ? <tr><td colSpan={5} className="py-10 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></td></tr>
              : !projectId ? <tr><td colSpan={5} className="py-10 text-center text-sm text-slate-400">Select a specific project from the top bar to view GRN-pending materials</td></tr>
              : items.length === 0 ? <tr><td colSpan={5} className="py-10 text-center text-sm text-slate-400">No GRN-pending materials</td></tr>
              : items.map((m) => (
                <tr key={m.id} className={`border-b border-slate-100 ${sel.has(m.id) ? "bg-blue-50/50" : "hover:bg-slate-50"}`}>
                  <td className="px-3 py-2.5"><Checkbox data-testid={`grn-select-${m.id}`} checked={sel.has(m.id)} onCheckedChange={() => toggle(m.id)} /></td>
                  <td className="px-4 py-2.5 font-mono text-xs font-medium text-slate-900">{m.material_number}</td>
                  <td className="px-4 py-2.5 text-sm"><Badge variant="secondary">{m.type_name}</Badge> <span className="text-xs text-slate-400">{m.subtype_name}</span></td>
                  <td className="px-4 py-2.5 text-sm text-slate-600">{m.make_name}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-500">{m.uploaded_date_received || "—"}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex items-center justify-between text-sm text-slate-500">
        <span>{total} pending</span>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}><ChevronLeft className="h-4 w-4" /></Button>
          <span>Page {page}/{totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      <Dialog open={grnOpen} onOpenChange={setGrnOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">GRN {sel.size} material(s)</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Receiving Store *</Label>
              <Select value={storeId} onValueChange={setStoreId}>
                <SelectTrigger data-testid="grn-store-select" className="mt-1"><SelectValue placeholder="Select store" /></SelectTrigger>
                <SelectContent>{stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.store_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Actual Material Received Date *</Label><Input data-testid="grn-date-input" type="date" max={new Date().toISOString().slice(0, 10)} value={recDate} onChange={(e) => setRecDate(e.target.value)} className="mt-1" /></div>
            <p className="text-xs text-slate-400">System GRN timestamp is recorded by the server and cannot be edited.</p>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setGrnOpen(false)}>Cancel</Button><Button data-testid="grn-confirm-btn" onClick={doGrn} className="bg-blue-600 hover:bg-blue-700">Confirm GRN</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={defOpen} onOpenChange={setDefOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">Mark {sel.size} Defective</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Assign Checker *</Label>
              <SearchableSelect testid="defective-checker-select" value={checkerId} onChange={setCheckerId} options={users.map((u) => ({ value: u.id, label: u.full_name, sub: u.role_name || u.email }))} placeholder="Select checker" className="mt-1" />
            </div>
            <div><Label>Defect Reason</Label>
              <SearchableSelect testid="defective-reason-select" value={reasonId} onChange={setReasonId} options={reasons.map((r) => ({ value: r.id, label: r.name }))} placeholder="Optional" className="mt-1" />
            </div>
            <div><Label>Remarks</Label><Input data-testid="defective-remarks-input" value={remarks} onChange={(e) => setRemarks(e.target.value)} className="mt-1" placeholder="Defect details" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDefOpen(false)}>Cancel</Button><Button data-testid="defective-confirm-btn" onClick={doDefective} className="bg-amber-600 hover:bg-amber-700">Mark Defective</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
